(function () {
  'use strict';

  const STATE = {
    customiserModal: null,
    customiserContent: null,
    customiserLoading: null,
    cartActionsSyncTimer: null,
    cartActionsSyncInFlight: false,
    cartActionsObserver: null,
    cartDrawerOpenObserver: null,
    removingCustomisationKeys: new Set(),
    latestCartPayload: null,
    pendingCartPayload: null,
    lastCartUpdatedAt: 0,
  };

  const CUSTOMISER_MODAL_ID = 'NewLogoCustomiserModal';
  const CART_ACTIONS_SYNCED_EVENT = 'new-logo:cart-actions-synced';
  const LEGACY_LOGO_PROPERTY_KEYS = new Set([
    'Logo Added',
    'Logo Positions',
    'Logo Method',
    'Logo Size',
    'Logo Type',
    'Logo Setup Required',
    'Logo Artwork',
    'Logo Artwork Filename',
    'Logo Notes',
    'Logo Group',
    'Logo Placement',
  ]);

  function config() {
    return window.NewLogoConfig || {};
  }

  function emitCartActionsSynced(reason) {
    document.dispatchEvent(
      new CustomEvent(CART_ACTIONS_SYNCED_EVENT, {
        detail: {
          reason: String(reason || ''),
          at: Date.now(),
        },
      })
    );
  }

  function normalizeGroup(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getCartPayload(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    if (!Array.isArray(candidate.items)) return null;
    return candidate;
  }


  function ensureCustomiserModal() {
    if (STATE.customiserModal) return STATE.customiserModal;

    const modal = document.createElement('basic-modal');
    modal.className = 'drawer modal cart-logo-modal';
    modal.id = CUSTOMISER_MODAL_ID;
    modal.setAttribute('hidden', '');
    modal.innerHTML = `
      <div class="fixed-overlay" aria-controls="${CUSTOMISER_MODAL_ID}"></div>
      <div class="drawer__inner">
        <button
          class="drawer__close-btn z-2"
          aria-controls="${CUSTOMISER_MODAL_ID}"
          aria-label="Close"
          type="button"
        >
          ×
        </button>
        <div class="drawer__body cart-logo-modal__body">
          <div class="cart-logo-modal__loading" data-logo-modal-loading role="status" aria-live="polite">
          <span class="visually-hidden">Loading the logo customiser</span>
          <div class="logo-skeleton" aria-hidden="true">
            <div class="logo-skeleton__title"></div>
            <div class="logo-skeleton__steps">
              <span class="logo-skeleton__step"></span>
              <span class="logo-skeleton__step"></span>
              <span class="logo-skeleton__step"></span>
              <span class="logo-skeleton__step"></span>
            </div>
            <div class="logo-skeleton__body">
              <div class="logo-skeleton__main">
                <div class="logo-skeleton__heading"></div>
                <div class="logo-skeleton__grid">
                  <span class="logo-skeleton__card"></span>
                  <span class="logo-skeleton__card"></span>
                  <span class="logo-skeleton__card"></span>
                  <span class="logo-skeleton__card"></span>
                  <span class="logo-skeleton__card"></span>
                  <span class="logo-skeleton__card"></span>
                </div>
              </div>
              <div class="logo-skeleton__aside">
                <div class="logo-skeleton__aside-product"></div>
                <div class="logo-skeleton__line"></div>
                <div class="logo-skeleton__line logo-skeleton__line--short"></div>
                <div class="logo-skeleton__line"></div>
              </div>
            </div>
          </div>
        </div>
          <div class="cart-logo-modal__content" data-logo-modal-content></div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    STATE.customiserModal = modal;
    STATE.customiserContent = modal.querySelector('[data-logo-modal-content]');
    STATE.customiserLoading = modal.querySelector('[data-logo-modal-loading]');

    modal.addEventListener('modal:handleAfterHide', () => {
      if (STATE.customiserContent) {
        STATE.customiserContent.innerHTML = '';
      }
      if (STATE.customiserLoading) {
        STATE.customiserLoading.hidden = false;
      }
    });

    return modal;
  }

  /**
   * Shown instead of navigating to /pages/add-logo when the modal cannot load.
   *
   * That page is only ever meant to be a source of markup for this modal —
   * sending a customer to it strands them on a bare page outside the basket
   * flow, so every failure path keeps them in the modal with a way out.
   */
  function showCustomiserLoadError(reason) {
    if (!STATE.customiserContent || !STATE.customiserLoading) return;
    STATE.customiserLoading.hidden = true;
    STATE.customiserContent.innerHTML = `
      <div class="cart-logo-modal__error" role="alert">
        <p class="cart-logo-modal__error-title">We couldn't open the logo customiser</p>
        <p class="cart-logo-modal__error-copy">${escapeHtml(
          reason || 'Something went wrong loading it. Please try again.'
        )}</p>
        <div class="cart-logo-modal__error-actions">
          <button type="button" class="btn btn--primary" data-logo-modal-retry>
            <span class="btn__text">Try again</span>
          </button>
          <a class="btn btn--secondary" href="${escapeHtml(
            window.NewLogoRuntime?.routes?.cartUrl || '/cart'
          )}">
            <span class="btn__text">Go to basket</span>
          </a>
        </div>
      </div>
    `;
  }

  function closeCustomiserModal() {
    if (!STATE.customiserModal) return;
    if (typeof STATE.customiserModal.hide === 'function') {
      STATE.customiserModal.hide();
      return;
    }
    STATE.customiserModal.removeAttribute('open');
    STATE.customiserModal.hidden = true;
  }

  /** True for /pages/add-logo and its per-profile variants (add-logo-fleece …). */
  function isAddLogoUrl(rawUrl) {
    const raw = String(rawUrl || '').trim();
    if (!raw) return false;
    try {
      const parsed = new URL(raw, window.location.origin);
      if (parsed.origin !== window.location.origin) return false;
      const pageConfig = config().pages || {};
      const prefix = String(pageConfig.prefix || 'add-logo-');
      const handle = parsed.pathname.replace(/\/+$/, '').split('/').pop() || '';
      return handle === 'add-logo' || handle.startsWith(prefix);
    } catch (error) {
      return false;
    }
  }

  function closeCartDrawerIfOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !cartDrawer.hasAttribute('open')) return;

    if (typeof cartDrawer.hide === 'function') {
      cartDrawer.hide();
      return;
    }

    cartDrawer.removeAttribute('open');
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    if (typeof cartDrawer.show === 'function') {
      cartDrawer.show();
      return;
    }

    cartDrawer.hidden = false;
    cartDrawer.setAttribute('open', '');
  }

  function isCartPageContext() {
    if (document.body.classList.contains('cart-template')) return true;
    if (document.querySelector('main-cart-items')) return true;
    return false;
  }

  function isCartDrawerOpen() {
    const cartDrawer = document.querySelector('cart-drawer');
    return Boolean(cartDrawer && cartDrawer.hasAttribute('open'));
  }

  function shouldSyncCartLineActions() {
    return isCartPageContext() || isCartDrawerOpen();
  }

  const CART_OPERATION_START_EVENT = 'new-logo:cart-operation-start';
  const CART_OPERATION_END_EVENT = 'new-logo:cart-operation-end';

  /**
   * Brackets a multi-step cart operation so the skeleton can cover all of it.
   *
   * Removing a customisation is not one write but several — the line change,
   * then the digitisation fee reconcile, then the re-render — and each used to
   * lift the skeleton on its own. The shopper saw skeleton, the old row, a
   * second skeleton, then the result. Between start and end the skeleton stays
   * up regardless of what the individual phases report, so there is one cover
   * and one reveal.
   */
  // The cart:updated we fire once an operation has settled. It exists so the
  // theme and any third-party listeners learn the final cart — our own modules
  // have already done their work and must ignore it, or they redo the entire
  // refresh a moment after the drawer has opened. That second pass is the
  // "cart refresh afterwards" the shopper sees.
  const SETTLED_SOURCE = 'new-logo-settled';

  // Concurrent /cart.js reads collapse into one request. A generation counter
  // bumped by every cart write means an in-flight read from before a write is
  // never shared with a caller that needs the post-write cart.
  const cartReadShare = { inFlight: null, generation: 0, startedAt: 0 };

  function bumpCartGeneration() {
    cartReadShare.generation += 1;
    cartReadShare.inFlight = null;
  }

  function shareCartRead(fetcher) {
    if (cartReadShare.inFlight && cartReadShare.startedAt === cartReadShare.generation) {
      return cartReadShare.inFlight;
    }
    cartReadShare.startedAt = cartReadShare.generation;
    const run = Promise.resolve()
      .then(fetcher)
      .finally(() => {
        // Only clear our own registration. A write may have replaced it with a
        // newer read; an orphaned pre-write read clearing that would kill the
        // dedup exactly when it matters.
        if (cartReadShare.inFlight === run) cartReadShare.inFlight = null;
      });
    cartReadShare.inFlight = run;
    return run;
  }

  function isCartOperationBusy() {
    return document.documentElement.getAttribute('data-new-logo-cart-busy') === '1';
  }

  /**
   * Waits for the next paint, but never blocks on it.
   *
   * requestAnimationFrame does not fire while the tab is hidden, so awaiting it
   * bare stalls the whole sequence: endCartOperation never runs and the cart
   * stays flagged busy — ignoring every later interaction — until a watchdog
   * rescues it. A shopper who clicks remove and switches tab hits exactly that.
   * Whichever of the frame or the timeout comes first wins.
   */
  function nextPaint(timeoutMs = 120) {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      requestAnimationFrame(finish);
      setTimeout(finish, timeoutMs);
    });
  }

  /**
   * Bounds a step so one stalled request cannot strand the sequence.
   *
   * Nothing in this feature uses AbortController or a fetch timeout, so every
   * await on the removal path can stay pending indefinitely — and a pending
   * promise is neither fulfilled nor rejected, so `catch` and `finally` are as
   * unreachable as the steps after it. Resolving on the deadline lets the
   * sequence finish with a beat-stale cart rather than not finish at all.
   */
  function withDeadline(promise, ms, label) {
    // The timer is cancelled when the step wins. Left running, it logged
    // "exceeded" after every step — including ones that settled instantly —
    // which made the console claim five overruns on a perfectly healthy run.
    let timer = null;
    let settled = false;
    return Promise.race([
      Promise.resolve(promise).then((value) => {
        settled = true;
        clearTimeout(timer);
        return value;
      }),
      new Promise((resolve) => {
        timer = setTimeout(() => {
          if (settled) return;
          console.warn(`[new-logo] ${label} exceeded ${ms}ms; continuing without it`);
          resolve();
        }, ms);
      }),
    ]).then((value) => {
      // A settled step is progress: push the inactivity watchdog back.
      touchCartOperation();
      return value;
    });
  }

  // removeStateKey -> restore that row's Remove button. Drained whenever an
  // operation ends, including by the watchdog, so a stalled request cannot
  // leave a permanently disabled button behind.
  const removalUiResets = new Map();

  document.addEventListener(CART_OPERATION_END_EVENT, () => {
    removalUiResets.forEach((reset) => {
      try {
        reset();
      } catch (error) {
        console.error(error);
      }
    });
    removalUiResets.clear();
  });

  /**
   * Waits for the theme's drawer re-render to actually land.
   *
   * dispatchExplicitCartRefresh only ASKS for a render; cart.js then fetches
   * sections and swaps the body on its own schedule. Finishing the sequence
   * without waiting meant the cover lifted, the theme's swap arrived a moment
   * later, and the skeleton re-covered — the shopper saw the row appear, then
   * go back to loading for another second or two.
   *
   * Resolves on the first body mutation plus a short settle, or on the
   * deadline. Never rejects: a theme that renders nothing must not strand us.
   */
  function waitForDrawerRender(timeoutMs = 1500, settleMs = 160) {
    return new Promise((resolve) => {
      const drawer = document.querySelector('cart-drawer');
      const body = drawer && drawer.querySelector('[id^="CartDrawerBody-"]');
      if (!body) {
        resolve();
        return;
      }

      let settleTimer = null;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(settleTimer);
        clearTimeout(hardStop);
        observer.disconnect();
        resolve();
      };

      const observer = new MutationObserver(() => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(finish, settleMs);
      });
      observer.observe(body, { childList: true, subtree: true });

      const hardStop = setTimeout(finish, timeoutMs);
    });
  }

  let cartOperationWatchdog = null;
  // Monotonic operation identity. The watchdog and every endCartOperation call
  // carry the id of the operation they belong to, so a stale finisher — a
  // sequence that outlived its watchdog, or an old timer — can no longer tear
  // down a newer operation.
  let cartOperationId = 0;

  /**
   * Re-arms the watchdog and lets the skeleton know work is still happening.
   *
   * The watchdog is an INACTIVITY timer, not a total-duration timer. The
   * sequence's per-step deadlines legitimately sum past any single figure
   * (audited at 8.2s of sanctioned budget against the old 4s total), so a
   * fixed overall timer fired mid-operation on any slow path — lifting the
   * cover and re-enabling buttons while writes were still in flight. Each
   * completed step now pushes the deadline back; only a step that truly
   * hangs past its own deadline lets the watchdog fire.
   */
  function touchCartOperation() {
    if (!isCartOperationBusy()) return;
    armCartOperationWatchdog(cartOperationId);
    document.dispatchEvent(new CustomEvent('new-logo:cart-operation-heartbeat'));
  }

  function armCartOperationWatchdog(opId) {
    clearTimeout(cartOperationWatchdog);
    cartOperationWatchdog = setTimeout(() => {
      if (opId !== cartOperationId) return;
      if (!isCartOperationBusy()) return;
      console.warn('[new-logo] cart operation watchdog fired; releasing the cart');
      endCartOperation('watchdog', opId);
    }, 4000);
  }

  function beginCartOperation(reason) {
    // The flag is what silences the reactive listeners in this module and in
    // new-logo-cart-pricing.js. Set it before anything else: a listener that
    // fires between the dispatch and the flag would restart the cascade.
    document.documentElement.setAttribute('data-new-logo-cart-busy', '1');
    cartOperationId += 1;

    // A timer armed before the operation began must not fire into it
    // un-suppressed — it was scheduled against a cart that no longer exists.
    if (STATE.cartActionsSyncTimer) {
      clearTimeout(STATE.cartActionsSyncTimer);
      STATE.cartActionsSyncTimer = null;
    }

    armCartOperationWatchdog(cartOperationId);
    return cartOperationId;
    document.dispatchEvent(
      new CustomEvent(CART_OPERATION_START_EVENT, { detail: { reason: String(reason || '') } })
    );
  }

  function endCartOperation(reason, opId) {
    // A finisher from an operation that is no longer current must not tear
    // down its successor.
    if (typeof opId === 'number' && opId !== cartOperationId) return;
    // Idempotent: the sequence ends normally and the `finally` calls this again.
    // Without the guard a second cart-operation-end event fires, which the
    // skeleton treats as another bracket closing.
    if (!isCartOperationBusy()) return;
    clearTimeout(cartOperationWatchdog);
    cartOperationWatchdog = null;
    document.documentElement.removeAttribute('data-new-logo-cart-busy');
    document.dispatchEvent(
      new CustomEvent(CART_OPERATION_END_EVENT, { detail: { reason: String(reason || '') } })
    );
  }

  function dispatchCartUpdatedFromPayload(cartPayload, source) {
    const safePayload = getCartPayload(cartPayload);
    if (!safePayload) return;

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: safePayload,
          source: String(source || 'new-logo-explicit'),
        },
      })
    );
  }

  function dispatchExplicitCartRefresh(open, reason) {
    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail: {
          open: Boolean(open),
          force: true,
          reason: String(reason || ''),
          source: 'new-logo-explicit',
        },
      })
    );
  }

  /**
   * `cart.js` binds its `cart:refresh` listener inside the cart *drawer*
   * component, so on /cart nothing re-renders. Removing a customisation
   * therefore left the row showing the old price and the customisation line
   * until the shopper reloaded. Re-render the cart section ourselves.
   */
  async function refreshCartPageSection() {
    if (!isCartPageContext()) return;

    const host =
      document.querySelector('.shopify-section[id$="__main-cart"]') ||
      document.querySelector('main-cart-items')?.closest('.shopify-section');
    if (!host || !host.id) return;

    const sectionId = host.id.replace(/^shopify-section-/, '');
    if (!sectionId) return;

    try {
      const response = await fetch(
        `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } }
      );
      if (!response.ok) return;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');

      // Swap only the inner container, exactly as the theme's own
      // cart-items#onCartUpdate does. Replacing the whole section destroys the
      // <main-cart> element itself, and its `cart:grouped-sections` listener is
      // never unregistered — so every refresh left another detached listener
      // behind, each one throwing "Cannot read properties of null (reading
      // 'id')" on the next dispatch because it can no longer find its section.
      const mainCart = document.getElementById(`MainCart-${sectionId}`);
      const nextMainCart = parsed.getElementById(`MainCart-${sectionId}`);

      if (mainCart && nextMainCart) {
        mainCart.innerHTML = nextMainCart.innerHTML;
        const wrapper = mainCart.closest('.cart');
        if (wrapper) {
          wrapper.classList.toggle('is-empty', !nextMainCart.querySelector('.cart-item'));
        }
      } else {
        const next = parsed.querySelector(`#${CSS.escape(host.id)}`) || parsed.querySelector('.shopify-section');
        if (!next) return;
        host.innerHTML = next.innerHTML;
      }

      // The swapped markup has no Add/Edit logo controls yet.
      scheduleCartLineActionsSync(0, true);
    } catch (error) {
      console.error(error);
    }
  }

  function hasSectionsPayload(candidate) {
    const safeCart = getCartPayload(candidate);
    return Boolean(safeCart && safeCart.sections && typeof safeCart.sections === 'object');
  }

  async function openCustomiserModalFromUrl(url) {
    const modal = ensureCustomiserModal();
    if (!STATE.customiserContent || !STATE.customiserLoading) return;

    STATE.customiserLastUrl = url;

    STATE.customiserLoading.hidden = false;
    STATE.customiserContent.innerHTML = '';
    if (typeof modal.show === 'function') {
      modal.show();
    } else {
      modal.hidden = false;
      modal.setAttribute('open', '');
    }

    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load logo customiser.');
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const customiserMarkup = parsed.querySelector('.logo-customiser');

      if (!customiserMarkup) {
        showCustomiserLoadError('The customiser could not be found for this item.');
        return;
      }

      const parsedUrl = new URL(url, window.location.origin);
      const customiserRootEl = customiserMarkup.querySelector('logo-customiser');
      if (customiserRootEl) {
        const line = parsedUrl.searchParams.get('line');
        const key = parsedUrl.searchParams.get('key');
        const group = parsedUrl.searchParams.get('customisation_group');
        const targetKeys = parsedUrl.searchParams.get('target_keys');

        if (line) customiserRootEl.setAttribute('data-target-line', line);
        if (key) customiserRootEl.setAttribute('data-target-key', key);
        if (group) customiserRootEl.setAttribute('data-customisation-group', group);
        if (targetKeys) customiserRootEl.setAttribute('data-target-keys', targetKeys);
        customiserRootEl.setAttribute('data-modal-mode', 'true');
        customiserRootEl.setAttribute('data-modal-id', CUSTOMISER_MODAL_ID);
      }

      STATE.customiserContent.innerHTML = customiserMarkup.outerHTML;

      STATE.customiserLoading.hidden = true;

      // Fade the real customiser in over the skeleton rather than swapping it
      // in on a single frame.
      STATE.customiserContent.classList.remove('is-revealed');
      void STATE.customiserContent.offsetWidth;
      STATE.customiserContent.classList.add('is-revealed');
    } catch (error) {
      console.error(error);
      showCustomiserLoadError('');
    }
  }

  function resolveGroupForCartItem(item) {
    const properties = item?.properties || {};
    const fromProps = normalizeGroup(properties._logo_customisation_group || properties['Logo Group'] || '');
    if (fromProps) return fromProps;

    const handle = String(item?.handle || '').trim();
    const mapped = normalizeGroup(config().products?.byHandle?.[handle] || '');
    if (mapped) return mapped;

    return '';
  }

  function getRuntimeOptionsForCartItem(item) {
    const runtime = window.NewLogoRuntime || {};
    const byHandle = runtime.customisationOptionsByHandle || {};
    const byProductId = runtime.customisationOptionsByProductId || {};

    const handle = String(item?.handle || '').trim();
    if (handle) {
      if (Object.prototype.hasOwnProperty.call(byHandle, handle)) {
        return byHandle[handle];
      }
      const lowerHandle = handle.toLowerCase();
      const matchedHandleKey = Object.keys(byHandle).find(
        (key) => String(key || '').trim().toLowerCase() === lowerHandle
      );
      if (matchedHandleKey) return byHandle[matchedHandleKey];
    }

    const productId = String(item?.product_id || '').trim();
    if (productId && Object.prototype.hasOwnProperty.call(byProductId, productId)) {
      return byProductId[productId];
    }

    return [];
  }

  function hasRuntimeCustomisationForCartItem(item) {
    const options = getRuntimeOptionsForCartItem(item);
    if (Array.isArray(options)) return options.length > 0;
    if (!options || typeof options !== 'object') return false;
    if (Array.isArray(options.options)) return options.options.length > 0;
    return Boolean(options.position_label || options.positionLabel || options.options);
  }

  function getLogoMethod(properties) {
    const method = String(properties?._logo_method || properties?.['Logo Method'] || '')
      .trim()
      .toLowerCase();
    if (method === 'print' || method === 'embroidery') return method;
    return '';
  }

  function getLogoType(properties) {
    const type = String(properties?._logo_type || properties?.['Logo Type'] || '')
      .trim()
      .toLowerCase();
    if (type === 'logo' || type === 'text') return type;
    return '';
  }

  /**
   * Personalised text lines carried no marker of their own in the cart, so a
   * text order was indistinguishable from an uploaded logo at a glance. The
   * `.new-logo-type-icon` styles already existed in new-logo-inject.css; only
   * the markup was missing.
   */
  /**
   * The artwork actually attached to a line. `_logo_artwork_urls` is the
   * canonical pipe-separated list; the numbered keys are the per-slot
   * fallbacks written alongside it. Only raster files can be shown as a
   * thumbnail — a PDF would render as a broken image.
   */
  /**
   * The hover bubble is drawn on <body> rather than inside the cart row.
   * The drawer scrolls and is transformed, so the in-row bubble was cropped by
   * the scroll box — and `position: fixed` would not have escaped either, since
   * a transformed ancestor becomes the containing block for fixed children.
   */
  let tooltipPortal = null;

  function getTooltipPortal() {
    if (tooltipPortal && tooltipPortal.isConnected) return tooltipPortal;
    tooltipPortal = document.createElement('span');
    tooltipPortal.className = 'new-logo-tooltip-portal';
    tooltipPortal.setAttribute('role', 'tooltip');
    document.body.appendChild(tooltipPortal);
    return tooltipPortal;
  }

  function hideTooltipPortal() {
    if (tooltipPortal) tooltipPortal.setAttribute('data-open', 'false');
  }

  function showTooltipPortal(trigger) {
    const source = trigger?.querySelector?.('.new-logo-tooltip');
    if (!source) return;

    const portal = getTooltipPortal();
    portal.innerHTML = source.innerHTML;
    portal.setAttribute('data-open', 'true');

    // Measure after painting the content, otherwise the box is still the
    // previous item's size and the clamping below uses stale numbers.
    const rect = trigger.getBoundingClientRect();
    const box = portal.getBoundingClientRect();
    const margin = 8;
    // clientWidth/clientHeight over innerWidth/innerHeight: they exclude the
    // scrollbar, so the bubble is not nudged under it.
    const viewWidth = document.documentElement.clientWidth || window.innerWidth || 0;
    const viewHeight = document.documentElement.clientHeight || window.innerHeight || 0;

    let left = rect.right - box.width;
    if (viewWidth) left = Math.min(left, viewWidth - box.width - margin);
    left = Math.max(margin, left);

    // Above the icons by preference; below when there is no room, which is the
    // case for the first row of the drawer.
    let top = rect.top - box.height - margin;
    if (top < margin) {
      const below = rect.bottom + margin;
      top = viewHeight ? Math.min(below, viewHeight - box.height - margin) : below;
    }
    top = Math.max(margin, top);

    portal.style.left = `${Math.round(left)}px`;
    portal.style.top = `${Math.round(top)}px`;
  }

  document.addEventListener('pointerover', (event) => {
    const trigger = event.target?.closest?.('.new-logo-method-icons[data-has-tooltip]');
    if (trigger) showTooltipPortal(trigger);
    else if (!event.target?.closest?.('.new-logo-tooltip-portal')) hideTooltipPortal();
  });
  document.addEventListener('pointerdown', hideTooltipPortal);
  document.addEventListener('focusin', (event) => {
    const trigger = event.target?.closest?.('.new-logo-method-icons[data-has-tooltip]');
    if (trigger) showTooltipPortal(trigger);
    else hideTooltipPortal();
  });
  // Anchored to a viewport position, so it has to follow or go away.
  window.addEventListener('scroll', hideTooltipPortal, true);
  window.addEventListener('resize', hideTooltipPortal);

  function getLogoArtworkUrls(properties) {
    const source = properties || {};
    // Union the slots with the joined list rather than picking one or the
    // other. A freshly uploaded file has no URL when _logo_artwork_urls is
    // built, so it only ever lands in its numbered slot — Shopify mints the
    // URL as it stores the upload. Reading the joined list alone dropped that
    // logo until a second save, by which point it had become an existing URL.
    // Slots lead because their index is the logo order; the joined list then
    // backfills older lines that predate the numbered slots.
    const joined = String(source._logo_artwork_urls || '').trim();
    const urls = [
      source._logo_artwork,
      source._logo_artwork_2,
      source._logo_artwork_3,
      ...(joined ? joined.split('|') : []),
      source._logo_artwork_url,
    ];

    const seen = new Set();
    return urls
      .map((url) => {
        const value = String(url || '').trim();
        // Saved logos on a customer's account come through Liquid as
        // /cdn/shop/files/... or //cdn.shopify.com/... just as often as a full
        // https:// URL. Demanding an absolute URL silently dropped every one
        // of them, so the drawer showed no artwork for signed-in shoppers.
        if (value.startsWith('//')) return `https:${value}`;
        return value;
      })
      .filter((url) => {
        if (!url || seen.has(url)) return false;
        if (!/^(https?:\/\/|\/[^/])/i.test(url)) return false;
        if (!/\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(url)) return false;
        seen.add(url);
        return true;
      })
      .slice(0, 3);
  }

  function getTypeIconMarkup(logoType) {
    if (logoType === 'text') {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M12.258 3h-8.51l-.083 2.46h.479c.26-1.544.758-1.783 2.693-1.845l.424-.013v7.827c0 .663-.144.82-1.3.923v.52h4.082v-.52c-1.162-.103-1.306-.26-1.306-.923V3.602l.431.013c1.934.062 2.434.301 2.693 1.846h.479z"/></svg>`;
    }
    return '';
  }

  function getMethodIconMarkup(method) {
    if (method === 'print') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false"><path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1"/><path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1"/></svg>`;
    }
    if (method === 'embroidery') {
      return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true" focusable="false"><path stroke-linejoin="round" d="M1.48291 6.99988c0.00643-1.39437 0.15366-2.81505 0.41703-4.56238h6.97872c0.26337 1.74733 0.4106 3.16827 0.41703 4.56264m-7.81251-0.01468c0.00642 1.39437 0.15366 2.81504 0.41703 4.56234l6.97818 0.0003c0.26337-1.74734 0.4106-3.16801 0.41703-4.56238"/><path stroke-linecap="round" stroke-linejoin="round" d="M5.38965 2.4375V0.625"/><path stroke-linecap="round" stroke-linejoin="round" d="m1.08691 2.4375 8.60656 0"/><path stroke-linecap="round" stroke-linejoin="round" d="m1.67284 4.62662 7.586 0.99368"/><path stroke-linecap="round" stroke-linejoin="round" d="M1.50703 7.80939 12.8991 9.32281"/><path stroke-linecap="round" stroke-linejoin="round" d="M1.08691 11.5625h8.60656"/><path stroke-linecap="round" stroke-linejoin="round" d="M5.38965 13.375v-1.8125"/></svg>`;
    }
    return '';
  }

  function getCartActionIconMarkup(action) {
    if (action === 'edit') {
      return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/><path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/></svg>`;
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-plus-circle" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/><path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/></svg>`;
  }

  function getRemoveCustomisationIconMarkup() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5"/></svg>`;
  }

  function titleCaseFromKey(value) {
    return String(value || '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function buildCustomisationTooltip(properties) {
    const source = properties || {};
    const positions = String(source._logo_positions || source['Logo Positions'] || '').trim();
    const method = String(source._logo_method || source['Logo Method'] || '').trim();
    const size = String(source._logo_size_tier || source['Logo Size'] || '').trim();

    const parts = [];
    if (positions) parts.push(`Positions: ${positions.replace(/\|/g, ', ')}`);
    if (method) parts.push(`Method: ${titleCaseFromKey(method)}`);
    if (size) parts.push(`Size: ${titleCaseFromKey(size)}`);

    // A text order previously showed only position/method/size, so there was
    // nothing on the cart saying what the customer had actually typed.
    if (getLogoType(source) === 'text') {
      const lines = [1, 2, 3]
        .map((index) => ({
          text: String(source[`_logo_text_line_${index}`] || '').trim(),
          // Falls back to the single colour that lines saved before per-line
          // colours carry for the whole block.
          colour: String(
            source[`_logo_text_colour_label_${index}`] || source._logo_text_colour_label || ''
          ).trim(),
        }))
        .filter((entry) => entry.text);

      if (lines.length) parts.push(`Text: ${lines.map((entry) => entry.text).join(' / ')}`);

      const font = String(source._logo_text_font_label || '').trim();
      if (font) parts.push(`Font: ${font}`);

      const colours = lines.map((entry) => entry.colour).filter(Boolean);
      const uniqueColours = Array.from(new Set(colours));
      if (uniqueColours.length === 1) {
        parts.push(`Colour: ${uniqueColours[0]}`);
      } else if (uniqueColours.length > 1) {
        // Naming the line matters once they differ — "Red, Black" alone does
        // not say which line is which.
        parts.push(
          `Colour: ${lines
            .map((entry, index) => `Line ${index + 1} ${entry.colour || '—'}`)
            .join(', ')}`
        );
      }
    }

    return parts.join('\n');
  }

  /**
   * Every Add/Edit control on the cart is drawn from this response, so when it
   * fails on a freshly loaded page the customisation UI is simply absent —
   * which reads to the shopper as "my logo has been lost" even though the cart
   * is intact server-side. Storefronts return 429 under load, and a single
   * dropped request should not be enough to blank the controls, so retry a
   * couple of times before giving up.
   */
  async function fetchCartJson() {
    return shareCartRead(() => fetchCartJsonUncached(0));
  }

  // Retries recurse on this, not on fetchCartJson: re-entering the sharer would
  // hand the retry back its own in-flight promise.
  async function fetchCartJsonUncached(attempt = 0) {
    const MAX_ATTEMPTS = 3;
    let response;

    try {
      response = await fetch(`/cart.js?_=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });
    } catch (error) {
      if (attempt + 1 < MAX_ATTEMPTS) {
        await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
        return fetchCartJsonUncached(attempt + 1);
      }
      throw error;
    }

    if (!response.ok) {
      // 429 and 5xx are worth another go; a 404 never will be.
      const worthRetrying = response.status === 429 || response.status >= 500;
      if (worthRetrying && attempt + 1 < MAX_ATTEMPTS) {
        await new Promise((resolve) => window.setTimeout(resolve, 400 * (attempt + 1)));
        return fetchCartJsonUncached(attempt + 1);
      }
      throw new Error(`Unable to load cart data (${response.status}).`);
    }

    return response.json();
  }

  async function getFreshCartPayload(changeResponseCart) {
    const fallbackPayload = getCartPayload(changeResponseCart);
    try {
      const freshCart = await fetchCartJson();
      if (fallbackPayload?.sections && typeof fallbackPayload.sections === 'object') {
        freshCart.sections = fallbackPayload.sections;
      }
      return freshCart;
    } catch (error) {
      return fallbackPayload || null;
    }
  }

  function collectGroupedSections() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    return sectionsToBundle;
  }

  function publishCartSectionsUpdate(cartPayload) {
    if (!cartPayload || typeof cartPayload !== 'object') return false;
    if (!cartPayload.sections || typeof cartPayload.sections !== 'object') return false;
    if (
      !window.FoxTheme ||
      !window.FoxTheme.pubsub ||
      !window.FoxTheme.pubsub.publish ||
      !window.FoxTheme.pubsub.PUB_SUB_EVENTS ||
      !window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
    ) {
      return false;
    }

    window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartPayload,
    });
    return true;
  }

  function clearLogoProperties(properties) {
    const source = properties || {};
    const nextProperties = {};

    Object.entries(source).forEach(([key, value]) => {
      const normalizedKey = String(key);
      const isLogoKey = normalizedKey.startsWith('_logo_') || LEGACY_LOGO_PROPERTY_KEYS.has(normalizedKey);
      if (isLogoKey) {
        nextProperties[normalizedKey] = '';
        return;
      }

      if (value === '' || value === null || typeof value === 'undefined') return;
      nextProperties[normalizedKey] = value;
    });

    return nextProperties;
  }

  function hasAnyLogoProperties(properties) {
    const source = properties || {};
    return Object.entries(source).some(([key, value]) => {
      const normalizedKey = String(key);
      if (!(normalizedKey.startsWith('_logo_') || LEGACY_LOGO_PROPERTY_KEYS.has(normalizedKey))) return false;
      if (value === '' || value === null || typeof value === 'undefined') return false;
      return true;
    });
  }

  /**
   * Cart writes retried with backoff. Shopify throttles the cart endpoints and
   * answers 429 once a shopper (or a burst of saves) goes too fast, which
   * surfaced as "too many attempts" and made add, edit and remove all fail at
   * once. /cart/change.js sets absolute state rather than a delta, so replaying
   * it is safe: the same request twice leaves the same line.
   */
  async function cartWrite(url, options, attempts = 3) {
    bumpCartGeneration();
    let response = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      // Each attempt is progress, not a hang — keep the watchdog honest
      // through a long Retry-After ladder.
      touchCartOperation();
      response = await fetch(url, options);
      // Shared reads begun before this write must not satisfy callers who ask
      // after it — the response is in, so the cart may have changed.
      bumpCartGeneration();
      if (response.ok) return response;
      const worthRetrying = response.status === 429 || response.status >= 500;
      if (!worthRetrying || attempt === attempts - 1) return response;
      // Honour Retry-After when Shopify sends one, otherwise back off.
      const after = Number.parseFloat(response.headers.get('Retry-After') || '');
      const waitMs = Number.isFinite(after) && after > 0
        ? Math.min(after * 1000, 8000)
        : Math.min(700 * 2 ** attempt, 5000);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
    return response;
  }

  /**
   * Shopify answers a rejected cart change with a reason — a property too long,
   * a stale line key, a sold-out variant. Throwing a fixed sentence discarded
   * it and left every failure looking identical, so pull the reason out.
   */
  async function describeCartError(response, fallback) {
    let detail = '';
    try {
      const body = await response.clone().json();
      detail = String(body?.description || body?.message || '').trim();
    } catch (error) {
      try {
        detail = (await response.clone().text()).trim().slice(0, 200);
      } catch (readError) {
        detail = '';
      }
    }
    const error = new Error(detail ? `${fallback} ${detail}` : `${fallback} (HTTP ${response.status})`);
    error.status = response.status;
    error.detail = detail;
    console.error('[logo] cart change failed', response.status, detail);
    return error;
  }

  async function changeLineProperties(id, quantity, properties) {
    const payload = {
      id,
      quantity,
      properties,
    };
    const sectionsToBundle = collectGroupedSections();
    if (sectionsToBundle.length) {
      payload.sections = sectionsToBundle;
      payload.sections_url = window.location.pathname;
    }

    const response = await cartWrite('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw await describeCartError(response, 'Unable to update this line item.');
    }

    return response.json();
  }

  function resolveItemForCustomisationRemoval(items, context) {
    const sourceItems = Array.isArray(items) ? items : [];
    const trimmedKey = String(context?.key || '').trim();
    const trimmedToken = String(context?.token || '').trim();
    const variantId = String(context?.variantId || '').trim();
    const allowTokenFallback = Boolean(context?.allowTokenFallback);
    const line = Number.isFinite(context?.line) ? context.line : Number.parseInt(String(context?.line || ''), 10);
    let keyFallback = null;
    let lineFallback = null;

    if (trimmedKey) {
      const byKey = sourceItems.find((item) => String(item?.key || '').trim() === trimmedKey);
      if (byKey && hasAnyLogoProperties(byKey?.properties || {})) return byKey;
      if (byKey) keyFallback = byKey;
    }

    if (Number.isFinite(line) && line > 0) {
      const byLine = sourceItems[line - 1] || null;
      if (byLine && hasAnyLogoProperties(byLine?.properties || {})) return byLine;
      if (byLine) lineFallback = byLine;
    }

    if (allowTokenFallback && trimmedToken) {
      const byToken = sourceItems.filter((item) => {
        const token = String(item?.properties?._logo_customiser_token || '').trim();
        if (!token || token !== trimmedToken) return false;
        if (!hasAnyLogoProperties(item?.properties || {})) return false;
        if (variantId && String(item?.variant_id ?? item?.id ?? '').trim() !== variantId) return false;
        return true;
      });
      if (byToken.length === 1) return byToken[0];
      if (byToken.length > 1) return byToken[0];
    }

    if (allowTokenFallback && variantId) {
      const byVariantWithLogo = sourceItems.filter((item) => {
        return String(item?.variant_id ?? item?.id ?? '').trim() === variantId && hasAnyLogoProperties(item?.properties || {});
      });
      if (byVariantWithLogo.length === 1) return byVariantWithLogo[0];
      if (byVariantWithLogo.length > 1) return byVariantWithLogo[0];

      const byVariantAny = sourceItems.filter((item) => {
        return String(item?.variant_id ?? item?.id ?? '').trim() === variantId;
      });
      if (byVariantAny.length === 1) return byVariantAny[0];
      if (byVariantAny.length > 1) return byVariantAny[0];
    }

    if (keyFallback) return keyFallback;
    if (lineFallback) return lineFallback;
    return null;
  }

  async function removeLineCustomisation(context) {
    const cart = await fetchCartJson();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return;

    let item = resolveItemForCustomisationRemoval(items, context);
    if (!item) {
      const fallbackLine = Number.isFinite(context?.line) ? context.line : Number.parseInt(String(context?.line || ''), 10);
      if (Number.isFinite(fallbackLine) && fallbackLine > 0 && items[fallbackLine - 1]) {
        item = items[fallbackLine - 1];
      }
    }
    if (!item) return cart;

    // Blank the keys, do not omit them. Shopify's /cart/change.js leaves an
    // omitted property untouched — only an empty string clears it. The old
    // first attempt omitted them, so it changed nothing, failed its own
    // still-has-logo check, and every removal paid for a second write of
    // ~700ms before the one that actually worked.
    const clearedProperties = clearLogoProperties(item.properties || {});
    const resolvedLine = items.findIndex((cartItem) => String(cartItem?.key || '') === String(item.key || '')) + 1;

    // Prefer key-based updates first; keys are stable for the exact line item we rendered.
    let changedCart = null;
    let keyUpdateFailed = false;
    try {
      changedCart = await changeLineProperties(item.key, item.quantity, clearedProperties);
    } catch (error) {
      keyUpdateFailed = true;
    }

    if (keyUpdateFailed && resolvedLine > 0) {
      const keyFailPayload = {
        line: resolvedLine,
        quantity: item.quantity,
        properties: clearedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        keyFailPayload.sections = sectionsToBundle;
        keyFailPayload.sections_url = window.location.pathname;
      }
      const keyFailResponse = await cartWrite('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(keyFailPayload),
      });
      if (!keyFailResponse.ok) {
        throw await describeCartError(keyFailResponse, 'Unable to remove customisation from this line item.');
      }
      changedCart = await keyFailResponse.json();
    } else if (keyUpdateFailed) {
      throw new Error('Unable to remove customisation from this line item.');
    }

    let changedItems = Array.isArray(changedCart?.items) ? changedCart.items : [];
    const changedByOldKey = changedItems.find((cartItem) => String(cartItem?.key || '') === String(item?.key || '')) || null;
    const changedByLine = resolvedLine > 0 ? changedItems[resolvedLine - 1] || null : null;
    const changedTarget = changedByOldKey || changedByLine;
    let stillHasLogo = hasAnyLogoProperties(changedTarget?.properties || {});

    if (!stillHasLogo) {
      return changedCart;
    }

    // Fallback: explicit line update and clear logo keys to blank values.
    if (resolvedLine > 0) {
      const payload = {
        line: resolvedLine,
        quantity: item.quantity,
        properties: clearedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle;
        payload.sections_url = window.location.pathname;
      }

      const response = await cartWrite('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Unable to remove customisation from this line item.');
      }

      changedCart = await response.json();
      changedItems = Array.isArray(changedCart?.items) ? changedCart.items : [];
      const fallbackByOldKey =
        changedItems.find((cartItem) => String(cartItem?.key || '') === String(item?.key || '')) || null;
      const fallbackByLine = resolvedLine > 0 ? changedItems[resolvedLine - 1] || null : null;
      const fallbackTarget = fallbackByOldKey || fallbackByLine;
      stillHasLogo = hasAnyLogoProperties(fallbackTarget?.properties || {});
      if (!stillHasLogo) return changedCart;
    }

    // Final fallback: clear by exact line when available, otherwise by key.
    // The line index is re-resolved against the POST-write cart: the earlier
    // writes may have collapsed or reordered lines, and replaying the
    // pre-write index here could blank a different line and stomp its
    // quantity with ours.
    const currentItems = Array.isArray(changedCart?.items) ? changedCart.items : items;
    const currentLine =
      currentItems.findIndex((cartItem) => String(cartItem?.key || '') === String(item?.key || '')) + 1;
    const fallbackLineIndex = currentLine > 0 ? currentLine : resolvedLine;
    if (fallbackLineIndex > 0) {
      const payload = {
        line: fallbackLineIndex,
        quantity: item.quantity,
        properties: clearedProperties,
      };
      const sectionsToBundle = collectGroupedSections();
      if (sectionsToBundle.length) {
        payload.sections = sectionsToBundle;
        payload.sections_url = window.location.pathname;
      }
      const response = await cartWrite('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Unable to remove customisation from this line item.');
      }
      return response.json();
    }

    return changeLineProperties(item.key, item.quantity, clearedProperties);
  }

  function buildCandidateCustomiserUrls(group, preferredUrl) {
    const pageConfig = config().pages || {};
    const mappedUrl = group ? pageConfig.map?.[group] : '';
    const prefix = String(pageConfig.prefix || 'add-logo-');
    const fallback = String(pageConfig.fallback || '/pages/add-logo');

    const candidates = [];
    if (preferredUrl) candidates.push(String(preferredUrl));
    if (mappedUrl) candidates.push(String(mappedUrl));
    if (group) {
      candidates.push(`/pages/${prefix}${group}`);
      candidates.push(`/pages/${group}`);
    }
    candidates.push(fallback);

    return Array.from(new Set(candidates.filter(Boolean)));
  }
  function resolveCustomiserUrlSync(group, preferredUrl) {
    const candidates = buildCandidateCustomiserUrls(group, preferredUrl);
    return candidates[0] || '/pages/add-logo';
  }

  function parseLineFromElement(itemEl) {
    const id = String(itemEl?.id || '');
    const match = id.match(/-(\d+)$/);
    if (match) {
      const line = Number.parseInt(match[1], 10);
      if (Number.isFinite(line) && line > 0) return line;
    }

    const indexInput = itemEl?.querySelector('[data-index]');
    const line = Number.parseInt(indexInput?.getAttribute('data-index') || '', 10);
    if (Number.isFinite(line) && line > 0) return line;

    return null;
  }

  const DIGITISATION_VARIANT_ID = '55420858270081';

  /** The variant a cart row is actually displaying, from its own markup. */
  function getElementVariantId(itemEl) {
    const attr = String(itemEl?.getAttribute('data-variant-id') || '').trim();
    if (attr) return attr;

    const link = itemEl?.querySelector('a[href*="variant="]');
    const href = String(link?.getAttribute('href') || '');
    const match = href.match(/[?&]variant=(\d+)/);
    return match ? match[1] : '';
  }

  function buildCartItemMapByElement(itemElements, cartItems) {
    const elements = Array.isArray(itemElements) ? itemElements : [];
    const items = Array.isArray(cartItems) ? cartItems : [];
    const byKey = new Map();
    const usedItemIndexes = new Set();
    const elementToItem = new Map();

    items.forEach((item, index) => {
      const key = String(item?.key || '').trim();
      if (!key || byKey.has(key)) return;
      byKey.set(key, { item, index });
    });

    // Prefer explicit key mapping if available.
    //
    // `data-cart-item-key` is stamped by this script on a previous sync. When
    // the cart gains or loses a line (the digitisation fee appearing is the
    // common case) the stamps are one row out of date, and every stale key
    // still exists in the cart — just against a different row. Matching on
    // them silently shifted every line: the fee's data landed on the first
    // garment, and each garment inherited its neighbour's customisation and
    // edit link. Only trust a stamp that still points at the same variant the
    // row is actually displaying.
    elements.forEach((itemEl) => {
      const key = String(itemEl?.getAttribute('data-cart-item-key') || '').trim();
      if (!key) return;
      const matched = byKey.get(key);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;

      const elementVariantId = getElementVariantId(itemEl);
      if (elementVariantId) {
        const itemVariantId = String(matched.item?.variant_id ?? matched.item?.id ?? '').trim();
        if (itemVariantId && itemVariantId !== elementVariantId) return;
      }

      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    const remainingElements = elements.filter((itemEl) => !elementToItem.has(itemEl));
    const remainingItems = items
      .map((item, index) => ({ item, index }))
      .filter((entry) => !usedItemIndexes.has(entry.index));

    const regularElements = [];
    const feeElements = [];
    // The theme does not put `data-variant-id` on a cart row, so this check
    // never identified the fee row and pushed it in with the garments. The
    // garment items were then paired one row late — the fee row took the first
    // garment's customisation and every row inherited its neighbour's.
    remainingElements.forEach((itemEl) => {
      if (getElementVariantId(itemEl) === DIGITISATION_VARIANT_ID) {
        feeElements.push(itemEl);
      } else {
        regularElements.push(itemEl);
      }
    });

    const regularItems = [];
    const feeItems = [];
    remainingItems.forEach((entry) => {
      const variantId = String(entry?.item?.variant_id ?? entry?.item?.id ?? '').trim();
      if (variantId === DIGITISATION_VARIANT_ID) {
        feeItems.push(entry);
      } else {
        regularItems.push(entry);
      }
    });

    regularElements.forEach((itemEl, index) => {
      const matched = regularItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    feeElements.forEach((itemEl, index) => {
      const matched = feeItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    return elementToItem;
  }

  function isGiftWrapItemElement(itemEl) {
    return Boolean(itemEl?.querySelector('.cart-quantity-gift-wrap'));
  }

  function ensureActionHost(itemEl) {
    const details = itemEl.querySelector('.cart-item__details') || itemEl.querySelector('.cart-item__product--info') || itemEl;
    if (!details) return null;

    let host = details.querySelector('.new-logo-action-host');
    if (!host) {
      host = document.createElement('div');
      host.className = 'new-logo-action-host';
    }

    // `querySelector` matches at any depth, so `.cart-item__action` is not
    // necessarily a direct child of `details`. Passing it to
    // details.insertBefore() threw NotFoundError and aborted the whole action
    // render, which is why the Add/Edit controls silently vanished.
    const actionRow = details.querySelector('.cart-item__action');
    const actionParent = actionRow?.parentElement || null;
    if (actionRow && actionParent) {
      if (host.parentElement !== actionParent || host.nextElementSibling !== actionRow) {
        actionParent.insertBefore(host, actionRow);
      }
    } else if (host.parentElement !== details) {
      details.appendChild(host);
    }

    return host;
  }

  function syncNativeLineBindings(itemEl, cartItem, line) {
    const safeLine = Number.parseInt(String(line || ''), 10);
    if (!Number.isFinite(safeLine) || safeLine <= 0) return;

    if (itemEl && String(itemEl.id || '').startsWith('CartDrawer-Item-')) {
      itemEl.id = `CartDrawer-Item-${safeLine}`;
    }

    const removeControl = itemEl?.querySelector('[is="cart-remove-item"], .cart-item__remove.btn-remove');
    if (removeControl) {
      removeControl.setAttribute('data-index', String(safeLine));
      const removeUrl = String(cartItem?.url_to_remove || '').trim();
      if (removeUrl) {
        removeControl.setAttribute('href', removeUrl);
      } else {
        removeControl.setAttribute('href', `/cart/change?line=${safeLine}&quantity=0`);
      }
    }

    const quantityInputs = itemEl?.querySelectorAll('.quantity__input[data-index], input[name="updates[]"][data-index]');
    quantityInputs?.forEach((input) => {
      input.setAttribute('data-index', String(safeLine));
    });
  }

  function upsertCartLineAction(itemEl, cartItem, line) {
    const properties = cartItem?.properties || {};
    if (properties._logo_charge_type != null || properties._logo_parent_key != null) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    if (isGiftWrapItemElement(itemEl)) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    const group = resolveGroupForCartItem(cartItem);
    const allowUngrouped = config().featureFlags?.allowUngroupedCartActions !== false;
    const hasRuntimeOptions = hasRuntimeCustomisationForCartItem(cartItem);
    const logoAddedValue = String(properties._logo_added || properties['Logo Added'] || '').toLowerCase();
    const hasCustomisationApplied =
      logoAddedValue === 'yes' || logoAddedValue === 'true' || logoAddedValue === '1' || getLogoMethod(properties) !== '';
    const isApplicable = hasCustomisationApplied || Boolean(group) || hasRuntimeOptions;

    if (!isApplicable) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    if (!group && !allowUngrouped && !hasCustomisationApplied) {
      itemEl.querySelector('.new-logo-cart-action')?.remove();
      return;
    }

    itemEl.querySelectorAll('.cart-item__options .new-logo-cart-action').forEach((staleAction) => staleAction.remove());

    const actionHost = ensureActionHost(itemEl);
    if (!actionHost) return;

    const actionMode = hasCustomisationApplied ? 'edit' : 'add';
    const method = getLogoMethod(properties);
    const logoType = getLogoType(properties);
    // A personalised-text line has no logo to edit, so "Edit Logo" was wrong.
    const editLabel = logoType === 'text' ? 'Edit Text' : 'Edit Logo';
    const triggerLabel = actionMode === 'edit' ? editLabel : 'Add Logo';
    const removeLabel = 'Remove customisation';
    const actionIconMarkup = getCartActionIconMarkup(actionMode);
    const removeIconMarkup = getRemoveCustomisationIconMarkup();
    const label = triggerLabel;
    const tooltip = hasCustomisationApplied ? buildCustomisationTooltip(properties) : '';
    // Keep the line breaks: the bubble renders them with `white-space: pre-line`
    // so each field gets its own row. Flattening to " • " ran everything into
    // one paragraph that wrapped mid-value and was hard to scan.
    const tooltipText = tooltip || '';
    const tooltipAria = tooltip ? tooltip.replace(/\n+/g, ', ') : '';
    const methodIconMarkup = hasCustomisationApplied && method
      ? `<div class="new-logo-method-icon new-logo-method-icon--${escapeHtml(method)}" aria-label="${escapeHtml(
          method
        )}">${getMethodIconMarkup(method)}</div>`
      : '';
    const typeIconMarkup =
      hasCustomisationApplied && getTypeIconMarkup(logoType)
        ? `<div class="new-logo-type-icon new-logo-type-icon--${escapeHtml(logoType)}" aria-label="${escapeHtml(
            logoType === 'text' ? 'Personalised text' : logoType
          )}">${getTypeIconMarkup(logoType)}</div>`
        : '';
    const artworkUrls = hasCustomisationApplied && logoType !== 'text' ? getLogoArtworkUrls(properties) : [];
    const thumbsMarkup = artworkUrls.length
      ? `<span class="new-logo-thumbs" aria-hidden="true">${artworkUrls
          .map((url) => `<img class="new-logo-thumb" src="${escapeHtml(url)}" alt="" loading="lazy" decoding="async">`)
          .join('')}</span>`
      : '';

    // Artwork last so it sits flush against the right edge, under the price.
    const iconsMarkup = `${typeIconMarkup}${methodIconMarkup}${thumbsMarkup}`;

    // A real element rather than `content: attr(data-tooltip)`. A pseudo-element
    // can only carry text, so the artwork could never appear inside the hover —
    // which is the whole point of showing it there.
    const declaredCount = Number.parseInt(String(properties._logo_artwork_count ?? '').trim(), 10);
    const missingArt =
      Number.isFinite(declaredCount) && declaredCount > artworkUrls.length
        ? declaredCount - artworkUrls.length
        : 0;

    const tooltipMarkup = tooltipText
      ? `<span class="new-logo-tooltip" role="tooltip">${
          artworkUrls.length
            ? `<span class="new-logo-tooltip__art">${artworkUrls
                .map(
                  (url) =>
                    `<img class="new-logo-tooltip__img" src="${escapeHtml(url)}" alt="" loading="lazy" decoding="async">`
                )
                .join('')}</span>`
            : ''
        }<span class="new-logo-tooltip__text">${escapeHtml(tooltipText)}${
          missingArt
            ? `\n+${missingArt} more logo${missingArt === 1 ? '' : 's'} (preview pending)`
            : ''
        }</span></span>`
      : '';

    const iconGroupMarkup = iconsMarkup
      ? `<div class="new-logo-method-icons"${
          tooltipText ? ' data-has-tooltip' : ''
        } aria-label="${escapeHtml(tooltipAria || method)}" tabindex="0">${iconsMarkup}${tooltipMarkup}</div>`
      : '';
    const urlBase = resolveCustomiserUrlSync(group, '');

    const url = new URL(urlBase, window.location.origin);
    url.searchParams.set('line', String(line));
    if (cartItem.key) {
      url.searchParams.set('key', String(cartItem.key));
    }
    if (group) {
      url.searchParams.set('customisation_group', group);
    }

    let actionWrap = actionHost.querySelector('.new-logo-cart-action');
    if (!actionWrap) {
      actionWrap = document.createElement('div');
      actionWrap.className = 'new-logo-cart-action';
      actionHost.appendChild(actionWrap);
    }

    const actionMarkup = `
      <div class="new-logo-cart-action__inline">
        <span class="new-logo-link__left">
          <span class="new-logo-link new-logo-link--static" aria-label="${escapeHtml(label)}"><span class="new-logo-link__label">${escapeHtml(label)}</span></span>
          <a class="btn--link new-logo-link new-logo-link--icon-trigger" href="${escapeHtml(
            url.toString()
          )}" data-new-logo-link data-line="${line}" data-key="${escapeHtml(String(cartItem.key || ''))}" data-group="${escapeHtml(
            group
          )}" aria-label="${escapeHtml(triggerLabel)}"><span class="new-logo-link__icon" aria-hidden="true">${actionIconMarkup}</span></a>
          ${
            hasCustomisationApplied
              ? `<button type="button" class="btn--link new-logo-link new-logo-link--icon-trigger new-logo-link--remove-trigger" data-new-logo-remove data-line="${line}" data-key="${escapeHtml(
                  String(cartItem.key || '')
                )}" data-logo-token="${escapeHtml(String(properties._logo_customiser_token || ''))}" data-variant-id="${escapeHtml(
                  String(cartItem.variant_id ?? cartItem.id ?? '')
                )}" aria-label="${escapeHtml(removeLabel)}"><span class="new-logo-link__icon" aria-hidden="true">${removeIconMarkup}</span></button>`
              : ''
          }
        </span>
        ${iconGroupMarkup}
      </div>
    `;
    const actionSignature = `${actionMode}|${url.toString()}|${method}|${logoType}|${tooltipText}|${group}|${artworkUrls.join(
      ','
    )}|${String(cartItem.key || '')}`;

    if (actionWrap.dataset.actionSignature !== actionSignature) {
      actionWrap.innerHTML = actionMarkup;
      actionWrap.dataset.actionSignature = actionSignature;
    }

    itemEl.setAttribute('data-cart-item-key', String(cartItem.key || ''));
    itemEl.setAttribute('data-customisation-group', group);
  }

  async function syncCartLineActions(cartHint, forceSync = false) {
    if (!forceSync && !shouldSyncCartLineActions()) {
      emitCartActionsSynced('skipped-context');
      return;
    }
    if (STATE.cartActionsSyncInFlight) {
      const queuedCart = getCartPayload(cartHint);
      if (queuedCart) {
        STATE.pendingCartPayload = queuedCart;
        STATE.latestCartPayload = queuedCart;
      }
      emitCartActionsSynced('already-running');
      return;
    }

    const cartItems = Array.from(document.querySelectorAll('.cart-item'));
    if (!cartItems.length) {
      emitCartActionsSynced('no-cart-items');
      return;
    }

    STATE.cartActionsSyncInFlight = true;

    try {
      const payloadCart =
        getCartPayload(cartHint) || (!forceSync ? getCartPayload(STATE.latestCartPayload) : null);
      const cart = payloadCart || (await fetchCartJson());
      if (payloadCart) {
        STATE.latestCartPayload = payloadCart;
      }

      const safeItems = Array.isArray(cart?.items) ? cart.items : [];

      // The digitisation fee is added to the cart after the cart page section
      // has already rendered, so the page is left a line short: the fee row is
      // simply absent until the shopper reloads. Draw it.
      if (isCartPageContext()) {
        const serverHasFee = safeItems.some(
          (item) => String(item?.variant_id ?? item?.id ?? '').trim() === DIGITISATION_VARIANT_ID
        );
        const pageHasFeeRow = cartItems.some((itemEl) => getElementVariantId(itemEl) === DIGITISATION_VARIANT_ID);
        const cooledDown = Date.now() - (STATE.lastFeeRowResyncAt || 0) > 5000;

        if (serverHasFee && !pageHasFeeRow && cooledDown) {
          STATE.lastFeeRowResyncAt = Date.now();
          refreshCartPageSection().catch((error) => console.error(error));
          emitCartActionsSynced('fee-row-missing');
          return;
        }
      }

      const elementMap = buildCartItemMapByElement(cartItems, safeItems);

      cartItems.forEach((itemEl) => {
        const matched = elementMap.get(itemEl) || null;
        const cartItem = matched?.item || null;
        if (!cartItem) return;

        const resolvedLine =
          (Number.isFinite(matched?.index) ? matched.index + 1 : null) || parseLineFromElement(itemEl) || 1;

        if (cartItem?.key) {
          itemEl.setAttribute('data-cart-item-key', String(cartItem.key));
        }

        syncNativeLineBindings(itemEl, cartItem, resolvedLine);
        upsertCartLineAction(itemEl, cartItem, resolvedLine);
      });
    } catch (error) {
      console.error(error);
    } finally {
      STATE.cartActionsSyncInFlight = false;
      emitCartActionsSynced('completed');
      const queuedCart = STATE.pendingCartPayload;
      if (queuedCart) {
        STATE.pendingCartPayload = null;
        syncCartLineActions(queuedCart, true).catch((error) => {
          console.error(error);
        });
      }
    }
  }

  function scheduleCartLineActionsSync(delay = 180, force = false, cartHint = null) {
    if (!force && !shouldSyncCartLineActions()) return;
    const payloadCart = getCartPayload(cartHint);
    if (payloadCart) {
      STATE.pendingCartPayload = payloadCart;
      STATE.latestCartPayload = payloadCart;
    } else if (force) {
      // For forced refresh cycles, avoid replaying stale cached payloads.
      STATE.pendingCartPayload = null;
    }

    if (STATE.cartActionsSyncTimer) {
      clearTimeout(STATE.cartActionsSyncTimer);
    }

    if (delay <= 0) {
      const queuedCart = STATE.pendingCartPayload;
      STATE.pendingCartPayload = null;
      syncCartLineActions(queuedCart, force).catch((error) => {
        console.error(error);
      });
      return;
    }

    STATE.cartActionsSyncTimer = setTimeout(() => {
      STATE.cartActionsSyncTimer = null;
      const queuedCart = STATE.pendingCartPayload;
      STATE.pendingCartPayload = null;
      syncCartLineActions(queuedCart, force).catch((error) => {
        console.error(error);
      });
    }, delay);
  }

  function setupCartActionHandlers() {
    document.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-new-logo-remove]');
      if (!removeButton) return;

      event.preventDefault();
      event.stopPropagation();

      const line = Number.parseInt(removeButton.getAttribute('data-line') || '', 10);
      const key = String(removeButton.getAttribute('data-key') || '').trim();
      const token = String(removeButton.getAttribute('data-logo-token') || '').trim();
      const variantId = String(removeButton.getAttribute('data-variant-id') || '').trim();
      const removeStateKey = key || (Number.isFinite(line) && line > 0 ? `line-${line}` : '') || token;
      if (!removeStateKey) return;
      if (STATE.removingCustomisationKeys.has(removeStateKey)) return;
      // One operation at a time, whichever row it is on. Two overlapping
      // brackets shared one flag and one watchdog, and whichever finished
      // first tore the other down mid-write.
      if (isCartOperationBusy()) return;

      STATE.removingCustomisationKeys.add(removeStateKey);
      const opId = beginCartOperation('remove-customisation');
      removeButton.classList.add('is-loading');
      removeButton.setAttribute('aria-disabled', 'true');
      if (removeButton instanceof HTMLButtonElement) {
        removeButton.disabled = true;
      }

      // Registered so the operation-end event can restore the button even if
      // the promise chain never settles. removeLineCustomisation awaits a bare
      // fetch before .then() runs, so a stall there bypasses .then, .catch and
      // .finally alike — and the guard above would then swallow every retry,
      // leaving this row's Remove button dead until the page reloads.
      const resetRemoveButton = () => {
        STATE.removingCustomisationKeys.delete(removeStateKey);
        removeButton.classList.remove('is-loading');
        removeButton.removeAttribute('aria-disabled');
        if (removeButton instanceof HTMLButtonElement) {
          removeButton.disabled = false;
        }
      };
      removalUiResets.set(removeStateKey, resetRemoveButton);

      removeLineCustomisation({
        line,
        key,
        token,
        variantId,
        allowTokenFallback: false,
      })
        .then(async (changedCart) => {
          // An explicit sequence, not a chain of reactions. Every listener that
          // used to respond to each step is suppressed while the operation flag
          // is set, so these run once each, in order, under the skeleton.

          // 1. Settle the digitisation fee against the line we just changed.
          //    The pricing module re-fetches the cart itself, so it always
          //    reconciles against server state rather than a passed payload.
          try {
            await withDeadline(window.NewLogoCartPricing?.reconcileNow?.(), 2200, 'reconcileNow');
          } catch (error) {
            console.error(error);
          }

          // 2. Render once, from whatever the cart now is.
          const keepOpen = isCartDrawerOpen();
          dispatchExplicitCartRefresh(keepOpen, 'remove-customisation');
          // Only wait for the drawer's re-render when the shopper can see the
          // drawer. On the cart page with the drawer shut the theme may never
          // touch the drawer body, and waiting for it just ate the full
          // timeout before the page section refreshed.
          if (keepOpen) await waitForDrawerRender();
          await withDeadline(refreshCartPageSection(), 1200, 'refreshCartPageSection');

          // 3. Re-inject the cart line controls over that render, once.
          //    changedCart, not null: getFreshCartPayload falls back to it if
          //    the fetch fails, instead of returning null and making the next
          //    step run the whole retry ladder a second time.
          const settledCart = await withDeadline(getFreshCartPayload(changedCart), 1200, 'getFreshCartPayload');
          await withDeadline(syncCartLineActions(settledCart, true), 1200, 'syncCartLineActions');

          // 3b. The render replaced the DOM the pricing was painted onto, and
          //     every repaint request after that is swallowed by the busy guard.
          //     Repaint over the finished render, once.
          try {
            await withDeadline(window.NewLogoCartPricing?.repaintNow?.(settledCart), 800, 'repaintNow');
          } catch (error) {
            console.error(error);
          }

          // 4. Let the render paint before uncovering.
          await nextPaint();
          endCartOperation('remove-customisation', opId);

          // 5. Only now tell everyone else, with the settled cart.
          if (settledCart) dispatchCartUpdatedFromPayload(settledCart, SETTLED_SOURCE);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          // Belt and braces. endCartOperation is idempotent, and this is the
          // only guarantee that the cart is released on every path.
          removalUiResets.delete(removeStateKey);
          resetRemoveButton();
          endCartOperation('remove-customisation-finally', opId);
        })
        .finally(() => {
          STATE.removingCustomisationKeys.delete(removeStateKey);
          removeButton.classList.remove('is-loading');
          removeButton.removeAttribute('aria-disabled');
          if (removeButton instanceof HTMLButtonElement) {
            removeButton.disabled = false;
          }
        });
    });

    // Catches `[data-new-logo-link]` and, as a backstop, any anchor pointing at
    // the add-logo page. That page exists purely to supply markup to this
    // modal, so a customer should never land on it.
    document.addEventListener('click', (event) => {
      const anchor = event.target.closest('a[href], [data-new-logo-link]');
      if (!anchor) return;

      const url = anchor.getAttribute('href') || '';
      const isLogoLink = anchor.matches('[data-new-logo-link]') || isAddLogoUrl(url);
      if (!isLogoLink || !url) return;

      event.preventDefault();
      closeCartDrawerIfOpen();

      openCustomiserModalFromUrl(url).catch((error) => {
        console.error(error);
        showCustomiserLoadError('');
      });
    });

    // Retry from the in-modal error state.
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-logo-modal-retry]')) return;
      event.preventDefault();
      const retryUrl = STATE.customiserLastUrl;
      if (!retryUrl) return;
      openCustomiserModalFromUrl(retryUrl).catch((error) => {
        console.error(error);
        showCustomiserLoadError('');
      });
    });

    const onLogoCustomiserSaved = async (event) => {
      closeCustomiserModal();

      if (document.body.classList.contains('cart-template')) {
        window.location.reload();
        return;
      }

      // A save must not silently vanish if a removal is still settling, and it
      // must not overlap it either — wait briefly for the cart to free up.
      for (let waited = 0; waited < 2000 && isCartOperationBusy(); waited += 100) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const opId = beginCartOperation('logo-customiser-save');

      // Same explicit sequence as removal: settle the fee, render once, inject
      // once, uncover. Nothing here reacts to anything. The whole body is
      // wrapped so the cart is released even if a step throws or is missing.
      let settledCart = null;
      try {
        // Open the drawer first, not last. The operation is already flagged, so
        // the skeleton covers it as it opens and the shopper sees a loading
        // cart resolve into the finished one — rather than a finished-looking
        // cart that visibly refreshes a moment later.
        openCartDrawer();

        try {
          await withDeadline(window.NewLogoCartPricing?.reconcileNow?.(), 2200, 'reconcileNow');
        } catch (error) {
          console.error(error);
        }

        dispatchExplicitCartRefresh(true, 'logo-customiser-save');
        await waitForDrawerRender();
        await withDeadline(refreshCartPageSection(), 1200, 'refreshCartPageSection');

        settledCart = await withDeadline(
          getFreshCartPayload(event?.detail?.cart || null),
          1200,
          'getFreshCartPayload'
        );
        await withDeadline(syncCartLineActions(settledCart, true), 1200, 'syncCartLineActions');

        // Repaint the pricing over the finished render — see the removal path.
        try {
          await withDeadline(window.NewLogoCartPricing?.repaintNow?.(settledCart), 800, 'repaintNow');
        } catch (error) {
          console.error(error);
        }

        await nextPaint();
      } catch (error) {
        console.error(error);
      } finally {
        endCartOperation('logo-customiser-save', opId);
      }

      if (settledCart) dispatchCartUpdatedFromPayload(settledCart, SETTLED_SOURCE);
    };
    window.addEventListener('logo-customiser:saved', onLogoCustomiserSaved);
    document.addEventListener('logo-customiser:saved', onLogoCustomiserSaved);

    document.addEventListener('cart:updated', (event) => {
      STATE.lastCartUpdatedAt = Date.now();
      // An open operation drives its own sync at the end. Reacting here is what
      // turned one removal into a cascade of re-syncs.
      if (isCartOperationBusy()) return;
      // Our own "settled" announcement: the work it would trigger is the work
      // that just finished.
      if (String(event?.detail?.source || '') === SETTLED_SOURCE) return;
      const cartPayload = getCartPayload(event?.detail?.cart);
      const force = String(event?.detail?.source || '') === 'logo-customiser';
      scheduleCartLineActionsSync(cartPayload ? 0 : 120, force, cartPayload);
    });

    document.addEventListener('cart:refresh', (event) => {
      if (isCartOperationBusy()) return;
      const shouldForce = Boolean(event?.detail?.open);
      scheduleCartLineActionsSync(360, shouldForce);
    });

    if (STATE.cartActionsObserver) {
      STATE.cartActionsObserver.disconnect();
    }
    STATE.cartActionsObserver = new MutationObserver((mutations) => {
      // The operation rewrites the cart DOM repeatedly; re-syncing on each of
      // those mutations was a large part of the event storm.
      if (isCartOperationBusy()) return;
      if (!shouldSyncCartLineActions()) return;

      const hasRelevantMutation = mutations.some((mutation) => {
        const targetNode = mutation.target;
        const element =
          targetNode && targetNode.nodeType === 1 ? targetNode : targetNode?.parentElement || null;
        if (!element || !(element instanceof Element)) return false;
        if (element.closest('.new-logo-cart-action')) return false;
        return Boolean(element.closest('cart-drawer, main-cart-items, .cart-template, .cart-item'));
      });

      if (!hasRelevantMutation) return;
      if (Date.now() - STATE.lastCartUpdatedAt < 420) return;
      scheduleCartLineActionsSync(260);
    });
    STATE.cartActionsObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (STATE.cartDrawerOpenObserver) {
        STATE.cartDrawerOpenObserver.disconnect();
      }
      STATE.cartDrawerOpenObserver = new MutationObserver((mutations) => {
        const opened = mutations.some(
          (mutation) => mutation.type === 'attributes' && mutation.attributeName === 'open' && cartDrawer.hasAttribute('open')
        );
        if (opened) {
          // The operation opens the drawer itself and runs its own sync at the
          // end; a forced sync 120ms in competed with it, uncovered.
          if (isCartOperationBusy()) return;
          scheduleCartLineActionsSync(120, true);
        }
      });
      STATE.cartDrawerOpenObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['open'],
      });
    }
  }

  function init() {
    setupCartActionHandlers();
    scheduleCartLineActionsSync(60);
  }

  init();

  window.NewLogoOverlay = {
    init,
    scheduleCartLineActionsSync,
  };
})();
